import { Link } from "react-router-dom";
import LegalLayout from "@/components/LegalLayout";
import ConsentFigure from "@/components/ConsentFigure";

const UL = ({ items }) => (
  <ul className="list-disc pl-5 space-y-2 marker:text-[#8b7cff]/70">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

const A = ({ href, children, ...p }) => (
  <a href={href} className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white" {...p}>{children}</a>
);

const sections = [
  {
    id: "acceptance",
    title: "Acceptance of these Terms",
    body: (
      <>
        <p>
          These Terms &amp; Conditions ("Terms") form a binding agreement between you (and, if applicable,
          the organization you represent) and Agentica AI Solutions ("Agentica," "we," "us," or "our"), a
          company based in Fort Lauderdale, Florida, United States. They govern your access to and use of
          agentica-sol.com (the "Site"), our consulting and engineering services, and our AI software
          products, including conversational agents (collectively, the "Services").
        </p>
        <p>
          By accessing the Site, submitting a form, purchasing or using a product, or otherwise using the
          Services, you agree to these Terms and to our{" "}
          <Link to="/privacy" className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white" data-testid="terms-to-privacy-link">Privacy Policy</Link>.
          If you do not agree, do not use the Services. If you are using the Services on behalf of an
          organization, you represent that you have authority to bind it.
        </p>
        <p>
          Where you have signed a separate master services agreement, statement of work, or order form with
          us, that document controls in the event of a conflict with these Terms.
        </p>
      </>
    ),
  },
  {
    id: "services",
    title: "Our Services & products",
    body: (
      <>
        <p>
          We provide (a) consulting and advisory services on GenAI and Agentic AI strategy, (b) custom
          software engineering and deployment, and (c) licensed AI products that we build and sell, which
          may be delivered as hosted software, APIs, or integrations.
        </p>
        <p>
          Specific deliverables, timelines, fees, acceptance criteria, and support terms are set out in the
          applicable proposal, statement of work, or order form. Unless otherwise agreed in writing, products
          are licensed, not sold, and are provided on a subscription basis.
        </p>
        <p>
          We may improve, modify, or discontinue features of the Services from time to time. For paid
          products we will provide reasonable notice of any material reduction in functionality.
        </p>
      </>
    ),
  },
  {
    id: "eligibility",
    title: "Eligibility & accounts",
    body: (
      <>
        <p>
          You must be at least 18 years old and able to form a binding contract to use the Services. If you
          create an account, you are responsible for keeping your credentials confidential, for all activity
          under your account, and for promptly notifying us of any unauthorized use.
        </p>
      </>
    ),
  },
  {
    id: "sms",
    title: "Text messaging program terms",
    body: (
      <>
        <p>
          Certain Agentica products ("Messaging Products") send and receive text messages (SMS/MMS) with end
          users. The following terms apply to any messaging program operated by us or deployed by our
          customers using our technology.
        </p>
        <UL items={[
          <><strong className="text-white/85">Consent first.</strong> We will not initiate a text conversation with any individual who has not first given express consent to receive messages from the relevant program, through a distinct, stand-alone SMS checkbox that is unchecked by default (never pre-selected and separate from terms acceptance), a keyword opt-in, verbal agreement, or another clear affirmative action. Consent is not a condition of any purchase.</>,
          <><strong className="text-white/85">Program description.</strong> Messages may include conversational replies from an AI agent, appointment or order updates, account alerts, customer-care responses, and — where separately consented — promotional messages. Message frequency varies by interaction.</>,
          <><strong className="text-white/85">Opt out.</strong> Reply <span className="font-display text-white tracking-wide">STOP</span> at any time to cancel. You will receive one confirmation message and no further messages from that program.</>,
          <><strong className="text-white/85">Help.</strong> Reply <span className="font-display text-white tracking-wide">HELP</span> for assistance, or contact ikumar@agentica-sol.com / +1 754 218 2448.</>,
          <><strong className="text-white/85">Costs.</strong> Message and data rates may apply. Carriers are not liable for delayed or undelivered messages.</>,
          <><strong className="text-white/85">Privacy.</strong> Phone numbers and opt-in data are never sold or shared with third parties for their marketing. See our Privacy Policy.</>,
          <><strong className="text-white/85">Automated interactions.</strong> You acknowledge that messages may be generated by an automated AI system and that you can request a human representative at any time.</>,
        ]} />
        <ConsentFigure />
        <p>
          <strong className="text-white/85">Customer obligations.</strong> If you are a business customer
          deploying a Messaging Product, you are solely responsible for obtaining and documenting valid
          consent from your end users, honoring opt-outs, providing accurate program disclosures, and
          complying with the Telephone Consumer Protection Act (TCPA), the CTIA Messaging Principles and Best
          Practices, carrier A2P 10DLC requirements, and all other applicable laws. You must not use the
          Messaging Products to send unsolicited, deceptive, or prohibited content (including SHAFT
          categories: sex, hate, alcohol, firearms, tobacco) or to message individuals who have opted out.
        </p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "Acceptable use",
    body: (
      <>
        <p>You agree not to, and not to permit others to:</p>
        <UL items={[
          "Use the Services in violation of any law, regulation, or third-party right.",
          "Send spam, unsolicited messages, or content that is harassing, defamatory, obscene, or discriminatory.",
          "Attempt to extract, reverse engineer, or circumvent the models, prompts, safety systems, or security of our products.",
          "Use outputs of our AI systems to develop competing foundation models, or represent AI-generated output as human-authored where disclosure is legally required.",
          "Upload malware, interfere with the integrity or performance of the Services, or access them by automated means without permission.",
          "Use the Services to make decisions that produce legal or similarly significant effects on individuals without appropriate human review.",
        ]} />
        <p>We may suspend or terminate access for violations of this section without liability.</p>
      </>
    ),
  },
  {
    id: "ai-output",
    title: "AI output & your responsibilities",
    body: (
      <>
        <p>
          Our products use generative AI. Outputs are probabilistic and may be inaccurate, incomplete, or
          inappropriate for your context. You are responsible for reviewing outputs before relying on them,
          for the decisions you make based on them, and for ensuring your use complies with applicable law and
          your own policies. AI output is not professional legal, medical, financial, or safety advice.
        </p>
        <p>
          You retain ownership of the content and data you provide ("Customer Data") and of the outputs
          generated for you, to the extent permitted by law. You grant us a limited license to process
          Customer Data solely to provide, secure, and improve the Services in accordance with our Privacy
          Policy and any applicable data processing agreement.
        </p>
      </>
    ),
  },
  {
    id: "ip",
    title: "Intellectual property",
    body: (
      <>
        <p>
          The Services — including software, models, prompts, designs, documentation, trademarks, and the
          Site's content — are owned by Agentica or its licensors and are protected by intellectual property
          laws. Except for the limited rights expressly granted in these Terms or a signed agreement, no
          license or right is granted to you.
        </p>
        <p>
          Ownership of custom deliverables created under a consulting or engineering engagement is governed
          by the applicable statement of work. Unless it states otherwise, we retain ownership of our
          pre-existing materials, tools, frameworks, and general know-how, and grant you a license to use them
          as embedded in the deliverables.
        </p>
        <p>
          If you send us feedback or suggestions, you grant us a perpetual, royalty-free license to use them
          without obligation to you.
        </p>
      </>
    ),
  },
  {
    id: "fees",
    title: "Fees & payment",
    body: (
      <>
        <p>
          Fees are set out in the applicable order form or statement of work. Unless otherwise stated, fees
          are quoted in U.S. dollars, exclusive of taxes, and due within thirty (30) days of invoice.
          Subscriptions renew automatically for successive terms unless either party gives notice of
          non-renewal before the renewal date. Late payments may accrue interest at 1.5% per month (or the
          maximum permitted by law, if lower), and we may suspend Services for accounts that are past due.
        </p>
      </>
    ),
  },
  {
    id: "confidentiality",
    title: "Confidentiality",
    body: (
      <>
        <p>
          Each party will protect the other's non-public information with at least reasonable care, use it
          only to perform under these Terms, and not disclose it except to personnel and advisors who need to
          know it and are bound by confidentiality obligations. These obligations do not apply to information
          that is public, independently developed, or lawfully obtained from a third party, and survive for
          three (3) years after the relationship ends (indefinitely for trade secrets).
        </p>
      </>
    ),
  },
  {
    id: "disclaimers",
    title: "Disclaimers",
    body: (
      <>
        <p className="uppercase text-xs tracking-wide text-white/50 leading-relaxed">
          Except as expressly stated in a signed agreement, the Services are provided "as is" and "as
          available." To the fullest extent permitted by law, Agentica disclaims all warranties, express or
          implied, including merchantability, fitness for a particular purpose, non-infringement, accuracy,
          and uninterrupted or error-free operation. We do not warrant that AI outputs will be accurate,
          complete, or suitable for any purpose, or that messages will be delivered by carriers.
        </p>
      </>
    ),
  },
  {
    id: "liability",
    title: "Limitation of liability",
    body: (
      <>
        <p className="uppercase text-xs tracking-wide text-white/50 leading-relaxed">
          To the fullest extent permitted by law, neither party will be liable for any indirect, incidental,
          special, consequential, or punitive damages, or for lost profits, revenue, data, or goodwill,
          arising out of or relating to these Terms, even if advised of the possibility. Agentica's total
          cumulative liability arising out of or relating to the Services will not exceed the fees paid by you
          to Agentica in the twelve (12) months preceding the event giving rise to the claim (or US $100 if
          no fees were paid). These limits do not apply to a party's indemnification obligations, breach of
          confidentiality, infringement of the other party's intellectual property, or liability that cannot
          be limited by law.
        </p>
      </>
    ),
  },
  {
    id: "indemnity",
    title: "Indemnification",
    body: (
      <>
        <p>
          You will defend, indemnify, and hold harmless Agentica and its officers, directors, employees, and
          contractors from any claims, damages, and expenses (including reasonable attorneys' fees) arising
          from (a) your Customer Data, (b) your use of the Services in violation of these Terms or applicable
          law, or (c) your messaging programs, including any failure to obtain valid consent or honor
          opt-outs. We will defend you against third-party claims that our products, as provided by us,
          infringe a U.S. patent, copyright, or trademark, subject to the exclusions and remedies set out in
          your signed agreement.
        </p>
      </>
    ),
  },
  {
    id: "termination",
    title: "Term & termination",
    body: (
      <>
        <p>
          These Terms apply while you use the Services. Either party may terminate a paid engagement as set
          out in the applicable agreement. We may suspend or terminate access immediately for material
          breach, suspected unlawful use, or non-payment. Upon termination, your right to use the Services
          ends, and sections that by their nature should survive (including IP, confidentiality, disclaimers,
          limitation of liability, indemnification, and governing law) will survive.
        </p>
      </>
    ),
  },
  {
    id: "law",
    title: "Governing law & disputes",
    body: (
      <>
        <p>
          These Terms are governed by the laws of the State of Florida, United States, without regard to its
          conflict-of-laws rules. The parties agree to the exclusive jurisdiction of the state and federal
          courts located in Broward County, Florida, for any dispute not subject to arbitration, and waive
          any objection to venue there.
        </p>
        <p>
          Before filing a claim, each party agrees to attempt in good faith to resolve the dispute informally
          by contacting the other party and allowing thirty (30) days for resolution. Nothing in this section
          prevents either party from seeking injunctive relief to protect its intellectual property or
          confidential information.
        </p>
      </>
    ),
  },
  {
    id: "general",
    title: "General terms & contact",
    body: (
      <>
        <p>
          These Terms, together with our Privacy Policy and any signed agreement, are the entire agreement
          between you and Agentica regarding the Services. If any provision is unenforceable, the remainder
          remains in effect. Our failure to enforce a provision is not a waiver. You may not assign these
          Terms without our consent; we may assign them in connection with a merger, acquisition, or sale of
          assets. Neither party is liable for delays caused by events beyond its reasonable control.
        </p>
        <p>
          We may update these Terms from time to time. The "Last updated" date indicates the latest revision;
          continued use after changes take effect constitutes acceptance.
        </p>
        <p>
          Agentica AI Solutions · Fort Lauderdale, Florida, United States ·{" "}
          <A href="mailto:ikumar@agentica-sol.com">ikumar@agentica-sol.com</A> · +1 754 218 2448
        </p>
      </>
    ),
  },
];

export default function TermsOfService() {
  return (
    <LegalLayout
      testId="terms-page"
      overline="Legal — Terms & Conditions"
      title="Clear terms for working together."
      intro="The agreement that governs use of the Agentica AI Solutions website, our consulting and engineering services, and the AI products we build and sell — including the consent-first rules for every text-messaging program powered by our technology."
      updated="July 2026"
      sections={sections}
    />
  );
}
